import { ALMACEN_CACHE, borrar, guardar, obtener } from './db';

/**
 * Copia local de lo último que devolvió el servidor.
 *
 * El caso que cubre: creaste la nota esta mañana con cobertura y ahora estás en
 * el cliente sin señal queriendo añadirle un ticket. Sin esta copia, la nota no
 * aparecería y no habría dónde meterlo.
 *
 * Es explícitamente una foto del pasado, no la verdad: la interfaz lo dice
 * cuando sirve datos de aquí.
 */

interface EntradaCache<T> {
  clave: string;
  datos: T;
  guardadoEn: number;
}

export async function guardarEnCache<T>(clave: string, datos: T): Promise<void> {
  await guardar<EntradaCache<T>>(ALMACEN_CACHE, {
    clave,
    datos,
    guardadoEn: Date.now(),
  });
}

/**
 * Tira la copia local de algo que ya no existe.
 *
 * Sin esto, borrar una nota la haría desaparecer de la pantalla y reaparecer al
 * quedarse sin cobertura, servida desde el caché como si nada hubiera pasado.
 */
export async function olvidarDelCache(clave: string): Promise<void> {
  await borrar(ALMACEN_CACHE, clave);
}

export async function leerDeCache<T>(
  clave: string,
): Promise<{ datos: T; guardadoEn: number } | null> {
  const entrada = await obtener<EntradaCache<T>>(ALMACEN_CACHE, clave);
  return entrada ? { datos: entrada.datos, guardadoEn: entrada.guardadoEn } : null;
}

/**
 * Pide al servidor y cachea; si falla, sirve la copia local.
 *
 * Devuelve `desdeCache` para que la pantalla pueda avisar en vez de fingir que
 * los datos están frescos.
 */
export async function conCache<T>(
  clave: string,
  peticion: () => Promise<T>,
): Promise<{ datos: T; desdeCache: boolean; guardadoEn?: number }> {
  try {
    const datos = await peticion();
    await guardarEnCache(clave, datos);
    return { datos, desdeCache: false };
  } catch (error) {
    const cacheada = await leerDeCache<T>(clave);
    if (cacheada) {
      return { datos: cacheada.datos, desdeCache: true, guardadoEn: cacheada.guardadoEn };
    }
    throw error;
  }
}
